import styled, { keyframes } from "styled-components";
import { Icon } from "@tomis-tech/chat-ui";

export type ToastVariant = "error" | "success";

interface ToastProps {
  message: string;
  onClose: () => void;
  visible: boolean;
  variant?: ToastVariant;
}

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{
  $visible: boolean;
  $variant: ToastVariant;
}>`
  position: absolute;
  bottom: 80px;
  left: 12px;
  right: 12px;
  background-color: ${({ theme, $variant }) =>
    $variant === "success" ? theme.colors.greenLight : theme.colors.warnLight};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === "success" ? theme.colors.green : theme.colors.warn};
  border-radius: ${({ theme }) => theme.radii[1]};
  padding: ${({ theme }) => theme.space[3]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space[3]};
  box-shadow: 0 4px 12px ${({ theme }) => theme.shadows[12]};
  z-index: 1000;
  animation: ${({ $visible }) => ($visible ? slideIn : slideOut)} 0.3s
    ease-in-out;
  animation-fill-mode: forwards;
`;

const Message = styled.span<{ $variant: ToastVariant }>`
  color: ${({ theme, $variant }) =>
    $variant === "success" ? theme.colors.green : theme.colors.warnDark};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: ${({ theme }) => theme.fontWeights[2]};
  line-height: ${({ theme }) => theme.lineHeights[1]};
  flex: 1;
`;

const CloseButton = styled.button<{ $variant: ToastVariant }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $variant }) =>
    $variant === "success" ? theme.colors.green : theme.colors.warnDark};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

export const Toast: React.FC<ToastProps> = ({
  message,
  onClose,
  visible,
  variant = "error",
}) => {
  if (!message) {
    return null;
  }

  return (
    <ToastContainer
      $visible={visible}
      $variant={variant}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <Message $variant={variant}>{message}</Message>
      <CloseButton
        onClick={onClose}
        aria-label="Close notification"
        type="button"
        $variant={variant}
      >
        <Icon name="close" size={16} aria-hidden="true" />
      </CloseButton>
    </ToastContainer>
  );
};
